import React from 'react';
import { Formik } from 'formik';
import { object, string } from 'yup';

import AgModalWindow from '@/components/common/ModalWindow/AgModalWindow';
import ActionButtonsWrapper from '@/components/common/ModalWindow/ActionButtonsWrapper/ActionButtonsWrapper';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import ModalFormDropdown from '@/components/business/ModalForm/ModalFormDropdown/ModalFormDropdown';
import ModalFormTextInput from '@/components/business/ModalForm/ModalFormTextInput/ModalFormTextInput';
import { UpliftIndex, UpliftIndexTypePascalCaseEnum, UpliftRegion } from '@/graphql/contracts/_generated_/contracts';
import { useAddUpliftIndex } from '../hooks/useAddUpliftIndex';

interface AddUpliftIndexProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRegion: UpliftRegion;
  regionOptions: Array<{ value: string; label: string; data: UpliftRegion }>;
  typeOptions: Array<{ value: UpliftIndexTypePascalCaseEnum; label: string }>;
  onSuccess: (newIndex: UpliftIndex) => void;
}

export default function AddUpliftIndexModal({
  isOpen,
  onClose,
  selectedRegion,
  regionOptions,
  typeOptions,
  onSuccess,
}: AddUpliftIndexProps) {
  const [addUpliftIndex, { loading }] = useAddUpliftIndex(selectedRegion?.name);

  const validationSchema = object().shape({
    name: string().required('Name is required').max(255, 'Max length is 255'),
  });

  return (
    <AgModalWindow
      isOpen={isOpen}
      onClose={onClose}
      title="Add Uplift Index"
      contentStyle={{ width: 340 }}
    >
      <Formik
        initialValues={{
          region: selectedRegion.name,
          type: UpliftIndexTypePascalCaseEnum.Custom,
          name: '',
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const res = await addUpliftIndex({
              variables: {
                input: {
                  name: values.name,
                  type: values.type,
                  upliftRegionId: selectedRegion.id!,
                },
              },
            });
            const newIndex = res?.data?.addUpliftIndex as UpliftIndex | undefined;
            if (newIndex) {
              onSuccess(newIndex);
            }
            onClose();
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit}>
            <ModalFormDropdown
              id="region"
              labelTitle="Region"
              requiredIcon={true}
              optionsData={regionOptions}
              selectedOption={{
                value: selectedRegion.name,
                label: selectedRegion.name,
                data: selectedRegion,
              }}
              disabled={true}
              className="mb-15"
              handleChange={() => {}}
              getOptionValue={(o: any) => String(o.value)}
              needToShowInputTextOnBlur={true}
            />
            <ModalFormDropdown
              id="type"
              labelTitle="Type"
              requiredIcon={true}
              optionsData={typeOptions}
              selectedOption={typeOptions.find(o => o.value === UpliftIndexTypePascalCaseEnum.Custom)}
              disabled={true}
              className="mb-15"
              handleChange={() => {}}
              getOptionValue={(o: any) => String(o.value)}
              needToShowInputTextOnBlur={true}
            />
            <ModalFormTextInput
              id="name"
              name="name"
              labelTitle="Name"
              requiredIcon={true}
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              validationMessage={formik.touched.name && formik.errors.name ? (formik.errors.name as string) : undefined}
              className="mb-15"
              placeholder="Enter name"
            />
            <ActionButtonsWrapper
              leftButton={(
                <Button
                  buttonStyleType={ButtonStyleType.SECONDARY}
                  type="button"
                  onClick={onClose}
                >
                  Cancel
                </Button>
              )}
              rightButton={(
                <Button
                  buttonStyleType={ButtonStyleType.PRIMARY}
                  type="submit"
                  disabled={!formik.isValid || formik.isSubmitting || loading}
                >
                  Save
                </Button>
              )}
            />
          </form>
        )}
      </Formik>
    </AgModalWindow>
  );
}
